import React from 'react';
import { Link } from 'react-router-dom';

const TradingPrinciples = () => {
  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-lg-8 mx-auto">
          <h1 className="mb-4">📈 Trading Principles</h1>
          
          <div className="card mb-4">
            <div className="card-body">
              <h3>Order Types</h3>
              <p>Understanding different order types is crucial for successful trading. Each serves a specific purpose in your trading strategy:</p>
              
              <div className="row mt-4">
                <div className="col-md-6">
                  <h5>Market Order</h5>
                  <p>Buy or sell immediately at the current market price.</p>
                  <p className="small text-muted">
                    Best used when: You want immediate execution and price isn't your primary concern. 
                    For highly liquid stocks, you'll typically get a price close to what you see quoted.
                  </p>
                </div>
                <div className="col-md-6">
                  <h5>Limit Order</h5>
                  <p>Buy or sell only at a specific price or better.</p>
                  <p className="small text-muted">
                    Best used when: You have a specific price target and are willing to wait. 
                    There's no guarantee your order will execute if the market doesn't reach your price.
                  </p>
                </div>
              </div>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Stop Order (Stop-Loss)</h5>
                  <p>Triggers a market order when price reaches a certain level.</p>
                  <p className="small text-muted">
                    Best used when: You want to limit potential losses or protect profits. 
                    Once triggered, it becomes a market order, so execution price may vary.
                  </p>
                </div>
                <div className="col-md-6">
                  <h5>Stop-Limit Order</h5>
                  <p>Combines features of stop and limit orders.</p>
                  <p className="small text-muted">
                    Best used when: You want to control both your trigger price and your execution price. 
                    More precise than a stop order, but may not execute in fast-moving markets.
                  </p>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  Imagine you own Tesla (TSLA) shares purchased at $200. The stock is currently trading at $250, 
                  but you're concerned about a potential market downturn. You could place a stop-loss order at $230 
                  to protect most of your gains. If Tesla drops to $230, your stop order triggers, selling your shares 
                  and locking in at least a $30 profit per share. However, if Tesla continues rising to $300, your 
                  stop order remains inactive, allowing you to capture those additional gains.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Trading Psychology</h3>
              <p>
                Emotions can be your biggest enemy in trading. The two most destructive emotions are fear and greed, 
                which often lead to irrational decisions.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Common Psychological Pitfalls</h5>
                  <ul>
                    <li>
                      <strong>FOMO (Fear Of Missing Out):</strong> Entering trades based on what others are doing
                    </li>
                    <li>
                      <strong>Loss Aversion:</strong> Holding losing positions too long hoping for recovery
                    </li>
                    <li>
                      <strong>Confirmation Bias:</strong> Only seeking information that supports your existing view
                    </li>
                    <li>
                      <strong>Overconfidence:</strong> Taking excessive risks after a series of successful trades
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Developing Mental Discipline</h5>
                  <ul>
                    <li>
                      <strong>Trading Plan:</strong> Create and stick to a written trading strategy
                    </li>
                    <li>
                      <strong>Trading Journal:</strong> Document all trades and review regularly
                    </li>
                    <li>
                      <strong>Risk Management:</strong> Never risk more than you can afford to lose
                    </li>
                    <li>
                      <strong>Patience:</strong> Wait for high-probability setups rather than forcing trades
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  During the 2020-2021 GameStop (GME) short squeeze, many traders bought in at prices above $300 due to FOMO, 
                  only to see the stock crash to below $50 within weeks. Those who maintained discipline and either avoided 
                  the hype or had clear exit strategies preserved their capital. Conversely, traders who had predetermined 
                  entry points at lower levels and exit strategies at higher prices were able to capture significant profits 
                  while managing risk.
                </p>
              </div>
            </div>
          </div>
          
          <div className="card mb-4">
            <div className="card-body">
              <h3>Technical vs. Fundamental Analysis</h3>
              <p>
                Traders typically rely on two main approaches to analyze potential trades:
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Technical Analysis</h5>
                  <p>
                    Studies price movements and patterns using charts and indicators to predict future price action.
                  </p>
                  <p className="small text-muted">
                    Common tools include moving averages, relative strength index (RSI), MACD, support/resistance levels, 
                    and chart patterns like head and shoulders or double bottoms.
                  </p>
                </div>
                <div className="col-md-6">
                  <h5>Fundamental Analysis</h5>
                  <p>
                    Evaluates a company's intrinsic value based on financial statements, industry position, and economic factors.
                  </p>
                  <p className="small text-muted">
                    Metrics include price-to-earnings (P/E) ratio, earnings growth, debt levels, competitive advantages, 
                    management quality, and industry trends.
                  </p>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In 2020, Zoom Video Communications (ZM) saw its stock price surge from $70 to over $560 as the pandemic 
                  drove massive adoption of video conferencing. Technical traders might have identified the strong uptrend 
                  and momentum indicators suggesting continued growth. Meanwhile, fundamental analysts noted Zoom's revenue 
                  grew 355% year-over-year and its user base expanded from 10 million to 300 million daily meeting participants. 
                  Both approaches identified a strong opportunity, albeit through different lenses.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Trading Timeframes</h3>
              <p>
                Different trading styles are defined by how long positions are typically held. Each timeframe requires 
                different strategies, tools, and mindsets.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Day Trading</h5>
                  <p>Positions opened and closed within the same trading day.</p>
                  <ul>
                    <li>Requires constant market monitoring</li>
                    <li>Uses short-term charts (1-minute to 1-hour)</li>
                    <li>Focuses on technical analysis and price action</li>
                    <li>Aims to capitalize on small price movements</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Swing Trading</h5>
                  <p>Positions held for several days to weeks.</p>
                  <ul>
                    <li>Less time-intensive than day trading</li>
                    <li>Uses daily and weekly charts</li>
                    <li>Combines technical and fundamental analysis</li>
                    <li>Targets larger price movements and trends</li>
                  </ul>
                </div>
              </div>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Position Trading</h5>
                  <p>Positions held for months to years.</p>
                  <ul>
                    <li>Most similar to long-term investing</li>
                    <li>Uses weekly and monthly charts</li>
                    <li>Heavily relies on fundamental analysis</li>
                    <li>Aims to capture major market trends</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Scalping</h5>
                  <p>Ultra-short-term trading with positions held for seconds to minutes.</p>
                  <ul>
                    <li>Requires split-second decision making</li>
                    <li>Uses tick charts and 1-minute charts</li>
                    <li>Focuses on order flow and market depth</li>
                    <li>Aims for many small profits throughout the day</li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  During the volatile market conditions of March 2020, different trading timeframes yielded vastly different results. 
                  Day traders could capitalize on the extreme intraday volatility, with the VIX (volatility index) reaching record levels. 
                  Some stocks were moving 10-20% within a single day, creating opportunities for quick profits. Meanwhile, position traders 
                  who maintained a long-term outlook and held quality companies through the downturn saw their positions recover and reach 
                  new highs by the end of 2020, demonstrating how different timeframes can be successful with the appropriate strategy.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Risk-to-Reward Ratio</h3>
              <p>
                The risk-to-reward ratio compares the potential profit of a trade to its potential loss. It's a fundamental 
                concept that helps traders evaluate whether a trade is worth taking.
              </p>
              
              <h5 className="mt-3">Calculating Risk-to-Reward</h5>
              <div className="row">
                <div className="col-md-6">
                  <p><strong>Formula:</strong> Risk-to-Reward Ratio = Potential Loss / Potential Profit</p>
                  <p>For example, if you risk $100 to potentially make $300:</p>
                  <p>Risk-to-Reward = $100/$300 = 1:3</p>
                </div>
                <div className="col-md-6">
                  <h6>Recommended Ratios</h6>
                  <ul>
                    <li>1:2 minimum for most trades</li>
                    <li>1:3 or better for optimal results</li>
                    <li>Lower ratios (e.g., 1:1) require higher win rates</li>
                  </ul>
                </div>
              </div>
              
              <h5 className="mt-3">Setting Targets and Stops</h5>
              <p>
                To implement risk-to-reward in practice, traders set:
              </p>
              <ul>
                <li><strong>Stop-Loss:</strong> Price level where you'll exit if the trade moves against you</li>
                <li><strong>Take-Profit:</strong> Price level where you'll exit to secure profits</li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  A trader spots Apple (AAPL) at $150 with support at $145 and resistance at $165. They buy at $150, setting a stop-loss 
                  at $144 (risking $6 per share) and a take-profit at $162 (aiming for $12 profit per share). This creates a 1:2 risk-to-reward 
                  ratio. Even if only 50% of their trades with this ratio are successful, they would still be profitable over time. This demonstrates 
                  how proper risk-to-reward planning can lead to overall profitability even when many individual trades result in losses.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Trading Volume and Liquidity</h3>
              <p>
                Volume represents the number of shares traded during a given time period, while liquidity refers to how easily 
                an asset can be bought or sold without affecting its price. Both are crucial considerations for traders.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Volume Analysis</h5>
                  <ul>
                    <li>
                      <strong>Volume Confirmation:</strong> Price moves with high volume are more significant
                    </li>
                    <li>
                      <strong>Volume Divergence:</strong> When price and volume trends move in opposite directions, suggesting potential reversal
                    </li>
                    <li>
                      <strong>Volume Indicators:</strong> On-Balance Volume (OBV), Volume Profile, and Volume-Weighted Average Price (VWAP)
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Liquidity Considerations</h5>
                  <ul>
                    <li>
                      <strong>Bid-Ask Spread:</strong> Tighter spreads indicate higher liquidity
                    </li>
                    <li>
                      <strong>Market Depth:</strong> The volume of orders at different price levels
                    </li>
                    <li>
                      <strong>Position Sizing:</strong> Larger positions require higher liquidity to enter and exit efficiently
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In September 2020, Tesla (TSLA) was not included in the S&P 500 index as many investors expected. The stock dropped 21% 
                  the following day on more than triple its average daily volume. This massive volume spike confirmed strong selling pressure 
                  and signaled a significant sentiment shift. However, Tesla's high liquidity (averaging over 50 million shares traded daily) 
                  allowed large institutional investors to exit positions without causing even more dramatic price declines. This demonstrates 
                  how volume can confirm price movements and how liquidity can impact price stability during significant events.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Trading Platforms and Tools</h3>
              <p>
                Modern traders have access to sophisticated platforms and tools that can significantly enhance trading performance. 
                Understanding the available options is essential for developing an effective trading setup.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Trading Platforms</h5>
                  <ul>
                    <li>
                      <strong>Professional Platforms:</strong> ThinkOrSwim, Interactive Brokers TWS, TradeStation
                      <p className="small text-muted">
                        Offer advanced charting, screening, and order execution capabilities
                      </p>
                    </li>
                    <li>
                      <strong>Retail Platforms:</strong> Robinhood, Webull, E*TRADE
                      <p className="small text-muted">
                        More user-friendly but with fewer advanced features
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Essential Trading Tools</h5>
                  <ul>
                    <li>
                      <strong>Charting Software:</strong> TradingView, StockCharts, MetaTrader
                    </li>
                    <li>
                      <strong>Screeners:</strong> Finviz, Trade Ideas, StockFetcher
                    </li>
                    <li>
                      <strong>News and Data:</strong> Bloomberg Terminal, Reuters, Yahoo Finance
                    </li>
                    <li>
                      <strong>Trading Journals:</strong> TraderSync, Edgewonk, Excel spreadsheets
                    </li>
                  </ul>
                </div>
              </div>
              
              <h5 className="mt-3">Setting Up Your Trading Workspace</h5>
              <p>
                An effective trading workspace typically includes:
              </p>
              <ul>
                <li>Multiple monitors for charts, watchlists, news, and order entry</li>
                <li>Reliable internet connection with backup options</li>
                <li>Customized layouts for different trading strategies</li>
                <li>Saved templates for quick analysis of new opportunities</li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  During the extreme market volatility of March 2020, many retail trading platforms experienced outages due to unprecedented 
                  volume. Traders using Robinhood faced multiple days where they couldn't access their accounts during critical market moves. 
                  Meanwhile, those with redundant setups (multiple platforms, backup internet connections) were able to continue trading. 
                  This event highlighted the importance of reliable trading infrastructure and having contingency plans. It also led many 
                  traders to upgrade from basic platforms to more robust professional solutions that could handle high-volume market conditions.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Position Sizing</h3>
              <p>
                Position sizing determines how much of your capital to allocate to each trade. Proper position sizing 
                is one of the most critical aspects of risk management and long-term trading success.
              </p>
              
              <h5 className="mt-3">Position Sizing Methods</h5>
              <div className="row">
                <div className="col-md-6">
                  <h6>Fixed Percentage Risk</h6>
                  <p>Risk a consistent percentage of your account on each trade (typically 1-2%).</p>
                  <p className="small text-muted">
                    Example: With a $50,000 account, risking 1% means you can lose a maximum of $500 per trade.
                  </p>
                </div>
                <div className="col-md-6">
                  <h6>Volatility-Based Sizing</h6>
                  <p>Adjust position size based on the asset's volatility (using ATR or standard deviation).</p>
                  <p className="small text-muted">
                    Smaller positions for more volatile assets, larger for less volatile ones.
                  </p>
                </div>
              </div>
              
              <h5 className="mt-3">Position Size Calculation</h5>
              <p>
                Position Size = (Account Size × Risk Percentage) ÷ (Entry Price - Stop Loss Price)
              </p>
              <p>
                This formula ensures you're risking the same percentage of your account regardless of the specific trade setup.
              </p>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  A trader with a $100,000 account wants to buy Microsoft at $300 with a stop loss at $285, risking 1% of their account. 
                  Using the position sizing formula: ($100,000 × 1%) ÷ ($300 - $285) = $1,000 ÷ $15 = 66.67 shares. 
                  They would buy 66 shares, which would result in a $990 loss if stopped out (very close to their 1% risk target). 
                  This disciplined approach ensures that no single trade can significantly damage their account, allowing them to 
                  withstand a series of losses while preserving capital for future opportunities.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Chart Patterns</h3>
              <p>
                Chart patterns are specific formations on price charts that can signal potential continuation or reversal of trends.
                Recognizing these patterns can help traders identify high-probability trading opportunities.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Reversal Patterns</h5>
                  <ul>
                    <li>
                      <strong>Head and Shoulders:</strong> Three peaks with the middle one highest, signaling a potential trend reversal
                    </li>
                    <li>
                      <strong>Double Top/Bottom:</strong> Two peaks/troughs at approximately the same level, indicating resistance/support
                    </li>
                    <li>
                      <strong>Rounding Bottom:</strong> Gradual shift from downtrend to uptrend, forming a "U" shape
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Continuation Patterns</h5>
                  <ul>
                    <li>
                      <strong>Flags and Pennants:</strong> Brief consolidation periods before continuing in the original trend direction
                    </li>
                    <li>
                      <strong>Triangles:</strong> Converging trendlines forming symmetric, ascending, or descending triangles
                    </li>
                    <li>
                      <strong>Cup and Handle:</strong> Cup-shaped consolidation followed by a smaller dip (handle) before continuing upward
                    </li>
                  </ul>
                </div>
              </div>
              
              <h5 className="mt-3">Trading Chart Patterns</h5>
              <ul>
                <li>Look for volume confirmation when patterns complete</li>
                <li>Wait for breakout/breakdown before entering trades</li>
                <li>Use pattern height to estimate potential price targets</li>
                <li>Place stops beyond key pattern levels to manage risk</li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In April 2020, Netflix (NFLX) formed a cup and handle pattern on its daily chart. The cup formed from March to April, 
                  with the handle developing in mid-April. When the stock broke above the handle resistance at around $440, it signaled 
                  a continuation of the uptrend. Traders who identified this pattern and entered on the breakout saw the stock climb to 
                  $485 within two weeks, a move of approximately 10%. The measured move target (calculated by adding the cup depth to the 
                  breakout point) accurately predicted the extent of the subsequent rally, demonstrating the practical value of chart pattern analysis.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Trading Indicators</h3>
              <p>
                Technical indicators are mathematical calculations based on price, volume, or open interest that help traders 
                analyze market conditions and make more informed decisions.
              </p>
              
              <div className="row mt-3">
                <div className="col-md-6">
                  <h5>Trend Indicators</h5>
                  <ul>
                    <li>
                      <strong>Moving Averages:</strong> Show average price over a specific period, smoothing price action
                      <p className="small text-muted">
                        Common periods: 20, 50, and 200-day moving averages
                      </p>
                    </li>
                    <li>
                      <strong>MACD (Moving Average Convergence Divergence):</strong> Shows relationship between two moving averages
                      <p className="small text-muted">
                        Signals include crossovers, divergences, and histogram changes
                      </p>
                    </li>
                    <li>
                      <strong>ADX (Average Directional Index):</strong> Measures trend strength regardless of direction
                      <p className="small text-muted">
                        Readings above 25 indicate strong trends
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Momentum and Oscillators</h5>
                  <ul>
                    <li>
                      <strong>RSI (Relative Strength Index):</strong> Measures speed and change of price movements on a scale of 0-100
                      <p className="small text-muted">
                        Traditional overbought/oversold levels at 70/30
                      </p>
                    </li>
                    <li>
                      <strong>Stochastic Oscillator:</strong> Compares closing price to price range over a period
                      <p className="small text-muted">
                        Useful for identifying potential reversals
                      </p>
                    </li>
                    <li>
                      <strong>Bollinger Bands:</strong> Shows price volatility with bands typically set at 2 standard deviations
                      <p className="small text-muted">
                        Contractions often precede significant moves
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              
              <h5 className="mt-3">Using Indicators Effectively</h5>
              <ul>
                <li>Avoid indicator redundancy (using multiple indicators that measure the same thing)</li>
                <li>Combine trend and momentum indicators for confirmation</li>
                <li>Use indicators appropriate for market conditions (trending vs. ranging)</li>
                <li>Remember that indicators are lagging by nature and based on historical data</li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In February 2021, the RSI for Bitcoin reached above 70 (overbought territory) while price was approaching $60,000. 
                  This overbought condition coincided with bearish divergence, where price made new highs but the RSI failed to make 
                  corresponding new highs. Traders who recognized this signal and either took profits or entered short positions benefited 
                  when Bitcoin subsequently corrected by over 20% in the following weeks. This example demonstrates how oscillators like 
                  RSI can help identify potential reversal points, especially when combined with divergence analysis.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Market Breadth</h3>
              <p>
                Market breadth indicators measure the participation of individual stocks in market movements, providing insight into 
                the underlying strength or weakness of market trends beyond what price indices alone reveal.
              </p>
              
              <h5 className="mt-3">Key Breadth Indicators</h5>
              <div className="row">
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Advance-Decline Line:</strong> Cumulative sum of advancing minus declining stocks
                      <p className="small text-muted">
                        Divergence from price can signal potential reversals
                      </p>
                    </li>
                    <li>
                      <strong>New Highs vs. New Lows:</strong> Number of stocks making 52-week highs versus lows
                      <p className="small text-muted">
                        Healthy uptrends show expanding new highs
                      </p>
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul>
                    <li>
                      <strong>Percentage of Stocks Above Moving Averages:</strong> Measures market participation
                      <p className="small text-muted">
                        Extreme readings can indicate overbought/oversold conditions
                      </p>
                    </li>
                    <li>
                      <strong>McClellan Oscillator:</strong> Measures rate of change in advancing versus declining issues
                      <p className="small text-muted">
                        Useful for identifying short to intermediate-term shifts
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              
              <h5 className="mt-3">Using Breadth in Trading</h5>
              <ul>
                <li>Confirm price trends with breadth indicators</li>
                <li>Watch for divergences between price and breadth</li>
                <li>Use extreme breadth readings to identify potential market turning points</li>
                <li>Monitor sector breadth to identify rotation opportunities</li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  In late 2021, while major indices like the S&P 500 continued making new highs, the percentage of stocks trading above 
                  their 200-day moving averages began declining significantly. By November, less than 50% of S&P 500 stocks were above 
                  their 200-day moving averages despite the index being near all-time highs. This breadth deterioration was an early 
                  warning sign of the market correction that followed in early 2022. Traders who monitored these breadth indicators were 
                  able to reduce exposure or implement hedging strategies before the broader market decline became apparent in the major indices.
                </p>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h3>Trading Journal and Performance Analysis</h3>
              <p>
                A trading journal is a systematic record of your trades that allows you to analyze your performance, 
                identify patterns in your trading behavior, and continuously improve your strategy.
              </p>
              
              <h5 className="mt-3">Essential Journal Components</h5>
              <div className="row">
                <div className="col-md-6">
                  <h6>Trade Details</h6>
                  <ul>
                    <li>Date and time of entry/exit</li>
                    <li>Symbol and position size</li>
                    <li>Entry and exit prices</li>
                    <li>Profit/loss (both absolute and percentage)</li>
                    <li>Strategy or setup used</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6>Analysis Components</h6>
                  <ul>
                    <li>Screenshots of charts before, during, and after the trade</li>
                    <li>Market conditions and context</li>
                    <li>Emotional state during the trade</li>
                    <li>What went right or wrong</li>
                    <li>Lessons learned and adjustments needed</li>
                  </ul>
                </div>
              </div>
              
              <h5 className="mt-3">Performance Metrics to Track</h5>
              <ul>
                <li>
                  <strong>Win Rate:</strong> Percentage of winning trades
                </li>
                <li>
                  <strong>Risk-Reward Ratio:</strong> Average profit on winners vs. average loss on losers
                </li>
                <li>
                  <strong>Expectancy:</strong> Average amount you can expect to win or lose per trade
                </li>
                <li>
                  <strong>Maximum Drawdown:</strong> Largest peak-to-trough decline in account value
                </li>
                <li>
                  <strong>Profit Factor:</strong> Gross profits divided by gross losses
                </li>
              </ul>
              
              <div className="example-box mt-3 p-3 bg-light border rounded">
                <h5 className="example-title"><i className="bi bi-lightbulb"></i> Real-World Example</h5>
                <p>
                  A trader noticed through journal analysis that their win rate on Monday mornings was significantly lower than other times (35% vs. 58% overall). 
                  Further investigation revealed they were trading too aggressively after weekend breaks when market gaps and volatility were higher. 
                  By adjusting their strategy to use smaller position sizes on Monday mornings and waiting for clearer setups, they improved their 
                  Monday win rate to match their overall average. This simple adjustment, identified through systematic journaling, increased their 
                  annual returns by approximately 12%. This demonstrates how data-driven analysis of trading patterns can lead to concrete improvements 
                  in trading performance.
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h3>Key Takeaways</h3>
              <div className="alert alert-info">
                <ul className="mb-0">
                  <li>Different order types serve specific purposes in your trading strategy</li>
                  <li>Managing emotions is often more important than market knowledge</li>
                  <li>Both technical and fundamental analysis have their place in trading decisions</li>
                  <li>Different trading timeframes require different strategies and approaches</li>
                  <li>A favorable risk-to-reward ratio is essential for long-term profitability</li>
                  <li>Volume and liquidity analysis provides crucial context for price movements</li>
                  <li>The right trading platforms and tools can significantly enhance performance</li>
                  <li>Proper position sizing is critical for managing risk and preserving capital</li>
                  <li>Chart patterns can help identify potential trading opportunities</li>
                  <li>Technical indicators provide objective measures of market conditions</li>
                  <li>Market breadth indicators reveal underlying strength or weakness in market trends</li>
                  <li>A detailed trading journal is essential for continuous improvement</li>
                  <li>Successful trading requires discipline, patience, and continuous learning</li>
                  <li>Always have a clear plan before entering any trade</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mt-4">
            <Link to="/risk-management" className="btn btn-primary-custom">
              Next: Risk Management →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingPrinciples;
